import React from "react";
import { Button, FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import { AddCircleIcon, CloseIcon, DeleteIcon, EditIcon, VolumeUpIcon } from "../../../../common/icon";
import translate from "../../../../../i18n/translate";

interface SearchedContentProps {
  searchedWord: {
    audioPath?: string;
    word: string;
    pos: string;
    definition?: string;
    translation?: string;
    sentence?: string;
    note?: string;
    isDeletable?: boolean;
  };
  handleClose: () => void;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  showAddToList: boolean;
  setShowAddToList: (show: boolean) => void;
  usersLists: Array<any>;
  selectedUserList: string;
  setSelectedUserList: any;
  handleAddWordToList: () => void;
  vocabularyContents: any; // Define more specifically based on actual store shape
  dispatch: any; // Define more specifically based on actual dispatch function
  setIsDeleteClicked: (clicked: boolean) => void;
  setIsEditClicked: (clicked: boolean) => void;
  setShowAddWordList: (show: boolean) => void;
}

const SearchedContent: React.FC<SearchedContentProps> = ({
  searchedWord,
  handleClose,
  isPlaying,
  setIsPlaying,
  showAddToList,
  setShowAddToList,
  usersLists,
  selectedUserList,
  setSelectedUserList,
  handleAddWordToList,
  vocabularyContents,
  dispatch,
  setIsDeleteClicked,
  setIsEditClicked,
  setShowAddWordList,
}) => {
  const changeFormat = (url: any) => {
    return url.replace(".wav", ".mp3");
  };

  const resetAddWordToListParams = () => {
    // Implement the logic to reset add word to list parameters
  };
  const handleAudioPlay = () => {
    const audioTag = document.createElement("audio");
    audioTag.src = `${process.env.REACT_APP_PROGRESS_URL}/content/${
      searchedWord.audioPath ? changeFormat(searchedWord.audioPath) : ""
    }`;
    if (!isPlaying) {
      audioTag.play();
    }
    audioTag.addEventListener("play", () => {
      setIsPlaying(true);
    });
    audioTag.addEventListener("ended", () => {
      setIsPlaying(false);
      audioTag.remove(); // Cleanup audio element
    });
  };

  return (
    <div className="searched-content-container">
      <div className="searched-content-header-wrapper">
        <div className="searched-content-header">
          <div className="searched-word-with-audio">
            {searchedWord.audioPath && searchedWord.audioPath !== "" && (
              <VolumeUpIcon
                className="text-audio-button"
                data-testid="text-audio-button"
                onClick={handleAudioPlay}
              />
            )}
            <p className="word-label">{searchedWord.word}</p>
          </div>
          <small className="word-type-label">{searchedWord.pos}</small>
        </div>
        <div className="close-icon-container">
          <CloseIcon data-testid="close-icon-button"  className="close-icon-button" onClick={handleClose} />
        </div>
      </div>
      <div className="searched-word-contents">
        <label className="container-label">{translate("vocab_center_definition_label")}:</label>
        <p className="definition-value">{searchedWord.definition ?? translate("vocab_center_no_definition_text")}</p>
        <label className="container-label">{translate("vocab_center_translation_label")}:</label>
        <p className="translation-value">{searchedWord.translation ?? translate("vocab_center_no_translation_text")}</p>
        <label className="container-label">{translate("vocab_center_sentence_label")}:</label>
        <p className="sentence-value">
          {searchedWord.sentence?.replace(/<[^>]*>/g, "") ?? translate("vocab_center_no_sentence_text")}
        </p>
        <label className="container-label">{translate("vocab_center_note_label")}</label>
        <p className="notes-value">{searchedWord.note ?? translate("vocab_center_no_note_text")}</p>
      </div>

      {showAddToList && (
        <div className="users-lists-container">
          {usersLists.length === 0 ? (
            <button
            type="button"
              className="remove-button-default-style create-new-wordlist-title"
              onClick={() => setShowAddWordList(true)}
            >
              There is no wordlist available, + {translate("vocab_center_add_wordlist")}
            </button>
          ) : (
            <FormControl className="user-list-drop-down">
              <InputLabel id="demo-simple-select-label" className="user-list-label">
                {translate("vocab_center_select_wordlist_text")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedUserList}
                onChange={(e) => setSelectedUserList(e.target.value)}
                className="user-list-select-action"
              >
                {usersLists.map((list: any) => (
                  <MenuItem value={list.listId} className="users-list-label" key={list.listId}>
                    {list.wordlist}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {vocabularyContents.isWordAddedToList && (
            <p className="word-added-to-list-success-msg">{translate("vocab_center_word_save_message")}</p>
          )}
          {vocabularyContents.errorOnAddingWordToList && vocabularyContents.errorOnAddingWordToList.code === 406 && (
            <p className="word-added-to-list-failure-msg">{translate("vocab_center_word_exists_error_message")}</p>
          )}
          <div className="user-list-saving-actions">
            <Button variant="contained" onClick={handleAddWordToList} disabled={usersLists.length === 0}>
              {translate("vocab_center_submit_text")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(resetAddWordToListParams());
                setShowAddToList(false);
              }}
            >
              {translate("vocab_center_close_text")}
            </Button>
          </div>
        </div>
      )}
      {!showAddToList && (
        <div className="searched-word-container-action">
          {searchedWord.isDeletable && (
            <Button variant="contained" className="delete-button" onClick={() => setIsDeleteClicked(true)}>
              <DeleteIcon className="delete-button-icon" />
              <span className="add-button-label">{translate("vocab_center_delete_text")}</span>
            </Button>
          )}
          <Button variant="contained" className="edit-button" onClick={() => setIsEditClicked(true)}>
            <EditIcon className="edit-button-icon" />
            <span className="edit-button-label">{translate("vocab_center_edit_text")}</span>
          </Button>
          <Button variant="contained" className="add-button" onClick={() => setShowAddToList(true)}>
            <AddCircleIcon className="add-button-icon" />
            <span className="add-button-label">Move</span>
          </Button>
        </div>
      )}

    </div>
  );
};

export default SearchedContent;
